
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const { currentLanguage, getLocalizedPath } = useLanguage();
  
  useEffect(() => {
    // Check if user has already accepted cookies
    const cookiesAccepted = localStorage.getItem('cookiesAccepted');
    if (!cookiesAccepted) {
      // Show the banner after a short delay
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, []);
  
  const acceptCookies = () => {
    localStorage.setItem('cookiesAccepted', 'true');
    setShowBanner(false);
  };
  
  const rejectCookies = () => {
    localStorage.setItem('cookiesAccepted', 'false');
    setShowBanner(false);
  };
  
  if (!showBanner) return null;
  
  const cookieText = {
    de: {
      title: 'Cookie-Hinweis',
      text: 'Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten. Durch die Nutzung unserer Seite stimmen Sie der Verwendung von Cookies zu.',
      accept: 'Akzeptieren',
      reject: 'Ablehnen',
      more: 'Mehr erfahren',
    },
    en: {
      title: 'Cookie Notice',
      text: 'We use cookies to ensure you get the best experience on our website. By using our site, you agree to our use of cookies.',
      accept: 'Accept',
      reject: 'Reject',
      more: 'Learn more',
    },
    fr: {
      title: 'Avis sur les cookies',
      text: 'Nous utilisons des cookies pour vous offrir la meilleure expérience sur notre site. En utilisant notre site, vous acceptez notre utilisation des cookies.',
      accept: 'Accepter',
      reject: 'Refuser',
      more: 'En savoir plus',
    },
    it: {
      title: 'Avviso sui cookie',
      text: 'Utilizziamo i cookie per offrirti la migliore esperienza sul nostro sito. Utilizzando il nostro sito, accetti il nostro utilizzo dei cookie.',
      accept: 'Accetta',
      reject: 'Rifiuta',
      more: 'Scopri di più',
    }
  };
  
  const t = cookieText[currentLanguage];
  
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#1A1F2C]/95 backdrop-blur-md border-t border-purple-500/30 shadow-lg shadow-purple-800/20 p-4 animate-in slide-in-from-bottom">
      <div className="container mx-auto max-w-6xl">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white mb-1">{t.title}</h3>
            <p className="text-sm text-gray-300 pr-4">
              {t.text}{' '}
              <Link to={getLocalizedPath('privacy')} className="text-purple-400 hover:text-purple-300 underline">
                {t.more}
              </Link>
            </p>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto justify-end">
            <Button
              variant="outline"
              size="sm"
              className="border-purple-500/30 text-gray-300 hover:bg-purple-900/20 hover:text-white"
              onClick={rejectCookies}
            >
              {t.reject}
            </Button>
            <Button
              size="sm"
              className="bg-purple-600 hover:bg-purple-700 text-white"
              onClick={acceptCookies}
            >
              {t.accept}
            </Button>
            <button
              onClick={rejectCookies}
              className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-purple-900/20 transition-colors"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
